import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType, TripStatus, TripType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  getMileageTrackingDrillDownTotalFilteredRecords,
  getMileageTrackingDrillDownViewState,
} from "@app/scene/dashboard/store/cards/mileageTracking/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { callApi } from "@app/store/api/callApi";
import { fetchDriveTimeCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyMileageTrackingDrillDownListViewSettings,
  exportMileageTrackingCardDrillDownData,
  fetchMileageTrackingCardData,
  fetchMileageTrackingCardDrillDownData,
  showMileageTrackingDrillDown,
  updateTrip,
} from "./actions";

const defaultColumns: string[] = [
  TripFieldName.START_TIME,
  TripFieldName.USER,
  TripFieldName.START,
  TripFieldName.FINISH,
  TripFieldName.RATE,
  TripFieldName.DISTANCE,
  TripFieldName.DURATION,
  TripFieldName.BUSINESS_COST,
];

const defaultSortColumn = TripFieldName.START_TIME;

function* onFetchMileageTrackingCardData({
  payload,
}: ReturnType<typeof fetchMileageTrackingCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;
  try {
    yield call(fetchDriveTimeCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showMileageTrackingDrillDown.request>) {
  const { configuration, customDateRange, scope } = payload;
  const { dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(getMileageTrackingDrillDownViewState);

  const viewState = localSettings.getViewSettings(`dashboard/mileageTracking`, tripFieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(defaultColumns, tripFieldModel),
    sort: [
      {
        field: tripFieldModel.getByName(defaultSortColumn)!,
        order: SortOrder.DESC,
      },
    ],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const isUserScope = !scope?.teamId && !!scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(TripFieldName.START_TIME, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);

  const teams: Team[] = yield select(getTeams);
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(
        CommonFieldName.TEAM,
        FilterOperator.IN_ANY,
        teams.find(({ id }) => id === scope.teamId)?.users.map(({ id }) => id) ?? []
      )
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }

  const currentUserId: User["id"] = yield select(getCurrentUserId);
  // when fetching for other users, only fetch business trips
  if (!isUserScope || scope?.userId !== currentUserId) {
    filter.conditions.push(
      createSimpleCondition(TripFieldName.TYPE, FilterOperator.EQUALS, TripType.BUSINESS)
    );
  }

  viewState.filter = filter;
  yield put(showMileageTrackingDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchMileageTrackingCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: activities logged card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyMileageTrackingDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(
      getMileageTrackingDrillDownViewState
    );

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/mileageTracking`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        status: TripStatus.COMPLETED,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          tripFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Trip> = yield callApi("fetchTrips", orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchMileageTrackingCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchMileageTrackingCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const drillDownViewState: DrillDownViewState = yield select(getMileageTrackingDrillDownViewState);
  const total: number = yield select(getMileageTrackingDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: EntityType.TRIP,
      total,
      viewState: drillDownViewState,
    })
  );
}

function* onUpdateTrip({ payload }: ReturnType<typeof updateTrip.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedTrip: Trip = yield callApi("updateTrip", orgId, payload.entity);
    if (payload.onSuccess) {
      yield call(payload.onSuccess, updatedTrip);
    }
    yield put(updateTrip.success(updatedTrip));
    yield put(notifyAboutChanges({ entityType: EntityType.TRIP, updated: [updatedTrip] }));
  } catch (error) {
    if (payload.onFailure) {
      yield call(payload.onFailure);
    }
    yield put(updateTrip.failure(error));
    yield put(handleError({ error }));
  }
}

export function* mileageTrackingSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchMileageTrackingCardData, onFetchMileageTrackingCardData);
  yield takeLatest(showMileageTrackingDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchMileageTrackingCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportMileageTrackingCardDrillDownData, onExport);
  yield takeLatest(exportMileageTrackingCardDrillDownData, onExport);
  yield takeEvery(updateTrip.request, onUpdateTrip);
}
